import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { INSURANCE_TYPES } from '../../../../../../../constants';
import { onboardingQuestionnaireActions } from '../../../../../../../store';
import {
  EXTRACTION_JOB_STATUS,
  INSURANCE_CARD_EXTRACTION_POLL_INTERVAL_SECONDS,
} from './constants';
import {
  INSURANCE_CARD_DOCUMENT_SUB_TYPES,
  INSURANCE_CARD_SIDES,
  INSURANCE_TYPE_TAB_IDS,
  INSURANCE_TYPE_TITLES,
} from '../constants';
import {
  buildExtractionInsurancePayload,
  findInsuranceByType,
  getInsuranceCardDocument,
  hasFilledInsuranceInformation,
  isExtractionInProgress,
  writeInsuranceExtractionEntry,
} from './utils';
import { useInsuranceCardExtractionEntry } from './useInsuranceExtractionSelectors';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { InsuranceType } from '../../../../../../../utils/types';
import type {
  InsuranceCardExtractionControls,
  InsuranceCardExtractionResult,
  InsuranceExtractionEntry,
} from './types';

export default function useInsuranceCardExtraction(
  insuranceType: InsuranceType,
  extractionEnabled: boolean,
): InsuranceCardExtractionControls {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();

  const documents = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.documents,
  );
  const insurances = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.onboardingQuestionnaireDetails?.insurances,
  );
  const tabSuccess = useSelector(
    (state: RootState) =>
      state.onboardingQuestionnaire.tabStatuses[INSURANCE_TYPE_TAB_IDS[insuranceType]].success,
  );
  const entry = useInsuranceCardExtractionEntry(insuranceType);
  const entryStatus = entry?.status;
  const entryJobId = entry?.jobId;

  const initializedRef = useRef(false);
  const lastPairRef = useRef<string | null>(null);
  const [needsOverwriteConfirmation, setNeedsOverwriteConfirmation] = useState(false);

  const documentSubTypes = INSURANCE_CARD_DOCUMENT_SUB_TYPES[insuranceType];
  const frontDocument = getInsuranceCardDocument(
    documents,
    documentSubTypes[INSURANCE_CARD_SIDES.FRONT],
  );
  const backDocument = getInsuranceCardDocument(
    documents,
    documentSubTypes[INSURANCE_CARD_SIDES.BACK],
  );
  const pairKey = frontDocument && backDocument ? `${frontDocument.id}:${backDocument.id}` : null;
  const hasInsuranceInformation = hasFilledInsuranceInformation(
    findInsuranceByType(insurances, insuranceType),
  );

  const persistEntry = useCallback(
    (next: InsuranceExtractionEntry | null) => {
      if (!onboardingQuestionnaireId) {
        return;
      }
      if (next) {
        dispatch(
          onboardingQuestionnaireActions.setInsuranceExtractionEntry({
            insuranceType,
            entry: next,
          }),
        );
      } else {
        dispatch(onboardingQuestionnaireActions.clearInsuranceExtractionEntry({ insuranceType }));
      }
      writeInsuranceExtractionEntry(onboardingQuestionnaireId, insuranceType, next);
    },
    [dispatch, insuranceType, onboardingQuestionnaireId],
  );

  const applySucceeded = useCallback(
    async (result: InsuranceCardExtractionResult) => {
      if (!onboardingQuestionnaireId) {
        return false;
      }
      const upsertAction =
        insuranceType === INSURANCE_TYPES.PRIMARY
          ? onboardingQuestionnaireActions.upsertOnboardingQuestionnairePrimaryInsurance
          : onboardingQuestionnaireActions.upsertOnboardingQuestionnaireSecondaryInsurance;
      const resultAction = await dispatch(
        upsertAction({
          onboardingQuestionnaireId,
          data: buildExtractionInsurancePayload(
            findInsuranceByType(insurances, insuranceType),
            result,
          ),
          getAccessTokenSilently,
        }),
      );
      return upsertAction.fulfilled.match(resultAction);
    },
    [dispatch, getAccessTokenSilently, insurances, insuranceType, onboardingQuestionnaireId],
  );

  const pollJob = useCallback(
    async (jobId: number) => {
      if (!onboardingQuestionnaireId) {
        return;
      }
      const resultAction = await dispatch(
        onboardingQuestionnaireActions.getInsuranceCardExtractionJob({
          onboardingQuestionnaireId,
          jobId,
          getAccessTokenSilently,
        }),
      );
      if (
        !onboardingQuestionnaireActions.getInsuranceCardExtractionJob.fulfilled.match(resultAction)
      ) {
        return;
      }

      const job = resultAction.payload;
      if (isExtractionInProgress(job.status)) {
        return;
      }

      if (job.status === EXTRACTION_JOB_STATUS.SUCCEEDED && job.result?.is_insurance_card) {
        if (!(await applySucceeded(job.result))) {
          persistEntry(null);
          return;
        }
        persistEntry({
          jobId,
          status: EXTRACTION_JOB_STATUS.SUCCEEDED,
          needsReview: true,
          reviewedAfterCompletion: false,
        });
        toast.success(
          `Insurance details filled from the card — please review the ${INSURANCE_TYPE_TITLES[insuranceType]} tab.`,
        );
        return;
      }

      persistEntry(null);
      toast.error(
        `We couldn't read the ${INSURANCE_TYPE_TITLES[insuranceType]} card — please enter the details manually.`,
      );
    },
    [
      applySucceeded,
      dispatch,
      getAccessTokenSilently,
      insuranceType,
      onboardingQuestionnaireId,
      persistEntry,
    ],
  );

  // Keep the latest pollJob in a ref so the polling effect can stay keyed on status+jobId alone
  // (and never restart from pollJob identity changes).
  const pollJobRef = useRef(pollJob);
  useEffect(() => {
    pollJobRef.current = pollJob;
  }, [pollJob]);

  const startExtraction = useCallback(async () => {
    if (!onboardingQuestionnaireId) {
      return;
    }
    const createdAction = await dispatch(
      onboardingQuestionnaireActions.createInsuranceCardExtractionJob({
        onboardingQuestionnaireId,
        insuranceType,
        getAccessTokenSilently,
      }),
    );
    if (
      !onboardingQuestionnaireActions.createInsuranceCardExtractionJob.fulfilled.match(
        createdAction,
      )
    ) {
      return;
    }
    persistEntry({
      jobId: createdAction.payload.job_id,
      status: EXTRACTION_JOB_STATUS.PENDING,
      needsReview: false,
      reviewedAfterCompletion: false,
    });
  }, [dispatch, getAccessTokenSilently, insuranceType, onboardingQuestionnaireId, persistEntry]);

  const confirmOverwrite = useCallback(() => {
    setNeedsOverwriteConfirmation(false);
    void startExtraction();
  }, [startExtraction]);

  const cancelOverwrite = useCallback(() => {
    setNeedsOverwriteConfirmation(false);
  }, []);

  // Trigger a new job when both card sides change to a new pair (initial upload or any replace).
  // On the first run AFTER the documents load, adopt the pre-existing pair WITHOUT triggering —
  // initializing while they are still undefined would make every page load look like a replace.
  // When the section already holds data, ask for confirmation first — extraction would overwrite
  // what the user typed.
  useEffect(() => {
    if (!extractionEnabled) {
      setNeedsOverwriteConfirmation(false);
      return;
    }
    if (documents == null) {
      return;
    }
    if (!initializedRef.current) {
      initializedRef.current = true;
      lastPairRef.current = pairKey;
      return;
    }
    if (pairKey == null) {
      lastPairRef.current = null;
      return;
    }
    if (pairKey === lastPairRef.current) {
      return;
    }
    lastPairRef.current = pairKey;
    if (hasInsuranceInformation) {
      setNeedsOverwriteConfirmation(true);
      return;
    }
    void startExtraction();
  }, [documents, extractionEnabled, pairKey, startExtraction, hasInsuranceInformation]);

  // Single polling owner: poll every 5s while the job is in progress (a freshly-created job OR a
  // PENDING/PROCESSING entry hydrated from localStorage on reload). Keyed only on status + jobId so
  // unrelated re-renders never restart it; the cleanup tears the interval down on a terminal status.
  // Gated on extractionEnabled — a completed poll writes to the backend.
  useEffect(() => {
    if (
      !extractionEnabled ||
      entryStatus == null ||
      !isExtractionInProgress(entryStatus) ||
      entryJobId == null
    ) {
      return undefined;
    }
    void pollJobRef.current(entryJobId);
    const intervalId = window.setInterval(() => {
      void pollJobRef.current(entryJobId);
    }, INSURANCE_CARD_EXTRACTION_POLL_INTERVAL_SECONDS * 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [entryStatus, entryJobId, extractionEnabled]);

  // Drop an in-progress entry that can never resolve while disabled; wait for load (documents != null).
  useEffect(() => {
    if (extractionEnabled || documents == null) {
      return;
    }
    if (entryStatus != null && isExtractionInProgress(entryStatus)) {
      persistEntry(null);
    }
  }, [documents, entryStatus, extractionEnabled, persistEntry]);

  // Clear the review block once the tab was visited post-completion AND all required fields are valid.
  useEffect(() => {
    if (entry?.needsReview && entry.reviewedAfterCompletion && tabSuccess) {
      persistEntry(null);
    }
  }, [entry?.needsReview, entry?.reviewedAfterCompletion, tabSuccess, persistEntry]);

  return { needsOverwriteConfirmation, confirmOverwrite, cancelOverwrite };
}
